import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, MapPin, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/lib/seo";
import { estNouvelleAnnonce, BadgeNouveau } from "@/lib/annonces";
import { BadgesMedias } from "@/components/BadgesMedias";
import { ApercuVideoSurvol } from "@/components/ApercuVideoSurvol";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Page locale : toutes les annonces d'une ville ou d'une commune, toutes rubriques. */
export default function VillePage() {
  const { villeSlug } = useParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [survolId, setSurvolId] = useState(null);

  useEffect(() => {
    setData(null);
    setErreur(false);
    axios
      .get(`${API}/villes/${villeSlug}`, { params: { limite: 48 } })
      .then((r) => setData(r.data))
      .catch(() => setErreur(true));
  }, [villeSlug]);

  const ville = data?.ville || villeSlug;
  useSeo({
    titre: `Annonces de rencontre à ${ville}`,
    description: `${data?.total || ""} annonces de rencontre à ${ville} sur FodindinCa : Lago, Love, ClubVIP et Air-Bengue. Photos en clair, contact direct dès l'abonnement.`.slice(0, 155),
    chemin: `/ville/${villeSlug}`,
  });

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-6 lg:px-8" data-testid="page-ville">
        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#E05638]">
          Annonces locales
        </span>
        <h1 className="mt-3 font-heading text-4xl font-extrabold text-stone-900 sm:text-5xl">
          Rencontres à {ville}
        </h1>
        <p className="mt-4 max-w-2xl text-base text-stone-600">
          Toutes les annonces actives à {ville}, toutes rubriques confondues. Les photos sont en
          clair ; les numéros restent réservés aux abonnés de la rubrique concernée.
        </p>

        {erreur ? (
          <p className="py-24 text-center text-stone-500" data-testid="ville-introuvable">
            Cette ville n'a pas encore de page dédiée.
          </p>
        ) : !data ? (
          <div className="flex justify-center py-24">
            <Loader2 className="animate-spin text-[#E05638]" size={34} />
          </div>
        ) : data.annonces.length === 0 ? (
          <div className="py-24 text-center" data-testid="ville-vide">
            <p className="text-stone-500">Aucune annonce à {ville} pour le moment.</p>
            <Link to="/inscription" className="mt-4 inline-flex items-center gap-2 font-semibold text-[#E05638]">
              Publiez la première <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="ville-grille">
            {data.annonces.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.45, delay: (i % 8) * 0.05 }}>
                <Link to={`/annonces/${a.slug}`} data-testid={`ville-annonce-${a.id}`}
                  className="group block overflow-hidden rounded-2xl border border-[#E5DFD5] bg-white transition-all hover:-translate-y-1 hover:shadow-lg">
                  <div
                    className="relative h-52 bg-[#EFE9DF]"
                    onMouseEnter={() => setSurvolId(a.id)}
                    onMouseLeave={() => setSurvolId(null)}
                  >
                    {a.photo ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${a.photo}`} alt={a.titre}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full items-center justify-center font-heading text-4xl font-extrabold text-stone-400">
                        {(a.titre || "?").charAt(0)}
                      </div>
                    )}
                    {estNouvelleAnnonce(a.date_debut) && (
                      <span className="absolute left-3 top-3"><BadgeNouveau testid={`ville-badge-${a.id}`} /></span>
                    )}
                    <BadgesMedias
                      nbPhotos={a.nb_photos || 0}
                      aVideo={Boolean(a.a_video)}
                      testid={`ville-medias-${a.id}`}
                    />
                    {a.a_video && (
                      <ApercuVideoSurvol
                        annonceId={a.id}
                        actif={survolId === a.id}
                        testid={`apercu-survol-${a.id}`}
                      />
                    )}
                    <span className="absolute bottom-3 left-3 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                      {a.rubrique}
                    </span>
                  </div>
                  <div className="space-y-1.5 p-4">
                    <h2 className="font-heading text-base font-extrabold leading-tight text-stone-900 line-clamp-2">
                      {a.titre}
                    </h2>
                    <p className="inline-flex items-center gap-1 text-xs text-stone-500">
                      <MapPin size={11} /> {a.lieu}
                    </p>
                    {a.texte && <p className="text-xs leading-relaxed text-stone-500 line-clamp-2">{a.texte}</p>}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
